<template>
  <div>
    <CRow class="form-group">
      <CCol
        tag="label"
        sm="3"
        class="col-form-label"
      >
        Tier Group
      </CCol>
      <CCol sm="9">
        <div v-if="isInitialLoading">
          <CSpinner
            color="primary"
            size="sm"
          /> Loading...
        </div>

        <CategoryTierGroup
          v-else-if="currentTierGroup && currentTierGroup.id"
          :tier-group="currentTierGroup"
          :current-category-id="category.id"
          :all-categories="allCategories"
          :is-loading="isLoading"
          collapsible
          :collapsed.sync="isCollapsed"
          @add-category="onAddCategoryToGroup"
          @remove-category="onRemoveCategory"
          @update-rpc-bounds="onUpdateRpcBounds"
          @update-status="onUpdateTierGroupStatus"
        />

        <div v-else>
          <div
            v-if="!isCurrentCategoryEligible"
            class="text-muted small"
          >
            Only Active and public categories can be added to a tier group.
          </div>
          <CButton
            v-else-if="!showAddDropdown"
            color="primary"
            size="sm"
            :disabled="isLoading"
            @click="onClickAddToTierGroup"
          >
            <CIcon name="cil-plus" /> Add to Tier Group
          </CButton>

          <div
            v-else
            class="d-flex align-items-center"
          >
            <div
              style="min-width: 280px;"
              class="mr-2"
            >
              <v-select
                v-model="selectedDropdownValue"
                :options="dropdownOptions"
                :reduce="opt => opt.value"
                label="label"
                placeholder="Select tier group..."
                :disabled="isLoading"
                @input="onDropdownChange"
              />
            </div>
            <CButton
              color="secondary"
              size="sm"
              :disabled="isLoading"
              @click="cancelAdd"
            >
              Cancel
            </CButton>
          </div>

          <div
            v-if="pendingExistingGroupId"
            class="mt-3 p-3 border rounded bg-light"
          >
            <h6>Set RPC bounds for this category</h6>
            <p class="text-muted small mb-2">
              Adding to <strong>{{ getTierGroupName(pendingExistingGroupId) }}</strong>. At least one bound must be set.
            </p>
            <div class="d-flex align-items-end">
              <div
                class="mr-2"
                style="width: 130px;"
              >
                <label class="small mb-1">Min RPC</label>
                <CInput
                  v-model.number="pendingBounds.minRpc"
                  type="number"
                  size="sm"
                  placeholder="0"
                  class="m-0"
                />
              </div>
              <div
                class="mr-2"
                style="width: 130px;"
              >
                <label class="small mb-1">Max RPC</label>
                <CInput
                  v-model.number="pendingBounds.maxRpc"
                  type="number"
                  size="sm"
                  placeholder="∞"
                  class="m-0"
                />
              </div>
              <CButton
                color="primary"
                size="sm"
                :disabled="!canConfirmExisting || isLoading"
                @click="confirmAddToExisting"
              >
                Add
              </CButton>
              <CButton
                color="secondary"
                size="sm"
                class="ml-1"
                :disabled="isLoading"
                @click="cancelPendingExisting"
              >
                Cancel
              </CButton>
            </div>
            <div
              v-if="pendingBoundsError"
              class="text-danger small mt-1"
            >
              {{ pendingBoundsError }}
            </div>
          </div>
        </div>
      </CCol>
    </CRow>

    <CreateTierGroupModal
      :show.sync="showCreateModal"
      :category-id="category.id"
      :category-name="category.name"
      :existing-names="existingTierGroupNames"
      :is-loading="isCreating"
      @submit="onCreateNewTierGroup"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import CategoryTierGroup from '@/components/CategoryTierGroup.vue'
import CreateTierGroupModal from '@/components/CreateTierGroupModal.vue'
import { rangesOverlap } from '@/utils/rpc-range'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'

const CREATE_NEW_OPTION = '__create_new__'

export default {
  name: 'CategoryTierGroupSection',

  components: {
    CategoryTierGroup,
    CreateTierGroupModal
  },

  props: {
    category: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isInitialLoading: false,
      isLoading: false,
      isCreating: false,
      isCollapsed: false,
      showAddDropdown: false,
      selectedDropdownValue: null,
      pendingExistingGroupId: null,
      pendingBounds: {
        minRpc: null,
        maxRpc: null
      },
      showCreateModal: false
    }
  },

  computed: {
    ...mapState('categoryTierGroup', {
      tierGroups: 'tierGroups',
      currentTierGroup: 'selectedRow'
    }),
    ...mapState('category', {
      allCategories: 'categories'
    }),

    dropdownOptions() {
      const groupOptions = this.tierGroups.map(g => ({
        value: g.id,
        label: g.name
      }))
      return [
        { value: CREATE_NEW_OPTION, label: '+ Create new tier group' },
        ...groupOptions
      ]
    },

    existingTierGroupNames() {
      return this.tierGroups.map(g => g.name).filter(Boolean)
    },

    isCurrentCategoryEligible() {
      return this.category?.status === 'Active' && !this.category?.isPrivate
    },

    boundsAreValid() {
      const min = this.pendingBounds.minRpc
      const max = this.pendingBounds.maxRpc
      if (min == null && max == null) {
        return false
      }

      if (min != null && max != null && Number(min) >= Number(max)) {
        return false
      }

      return true
    },

    pendingTierGroup() {
      if (!this.pendingExistingGroupId) {
        return null
      }

      return this.tierGroups.find(g => g.id === this.pendingExistingGroupId) || null
    },

    pendingOverlapError() {
      if (!this.pendingTierGroup) {
        return ''
      }

      const { minRpc, maxRpc } = this.pendingBounds
      if (minRpc == null && maxRpc == null) {
        return ''
      }

      const entries = this.pendingTierGroup.categories || []
      const overlap = entries.find(e => rangesOverlap(minRpc, maxRpc, e.minRpc, e.maxRpc))
      if (!overlap) {
        return ''
      }

      const cat = this.allCategories.find(c => c.id === overlap.categoryId)
      return `RPC range overlaps with "${cat ? cat.name : overlap.categoryId}".`
    },

    pendingBoundsError() {
      const min = this.pendingBounds.minRpc
      const max = this.pendingBounds.maxRpc
      if (min == null && max == null) {
        return ''
      }

      if (min != null && max != null && Number(min) >= Number(max)) {
        return 'Min RPC must be less than Max RPC.'
      }

      return this.pendingOverlapError
    },

    canConfirmExisting() {
      return this.pendingExistingGroupId && this.boundsAreValid && !this.pendingOverlapError
    }
  },

  watch: {
    'category.id': {
      immediate: true,
      handler() {
        this.isCollapsed = !!this.category?.categoryTierGroupId
        this.refresh()
      }
    },
    'category.categoryTierGroupId'(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.isCollapsed = false
      }

      this.refresh()
    }
  },

  methods: {
    ...mapActions('categoryTierGroup', {
      listTierGroups: 'list',
      findTierGroup: 'findOne',
      clearSelectedTierGroup: 'clearSelected',
      createTierGroup: 'create',
      updateTierGroup: 'update',
      addCategoryToTierGroup: 'addCategory',
      removeCategoryFromTierGroup: 'removeCategory',
      updateRpcBoundsAction: 'updateRpcBounds'
    }),
    ...mapActions('category', {
      findCategory: 'findOne',
      listCategories: 'list'
    }),

    async refresh() {
      if (!this.category?.id || this.isInitialLoading) {
        return
      }

      this.isInitialLoading = true
      try {
        const tasks = []

        if (!this.allCategories?.length) {
          tasks.push(this.listCategories())
        }

        if (this.category.categoryTierGroupId) {
          tasks.push(this.findTierGroup(this.category.categoryTierGroupId))
        } else {
          this.clearSelectedTierGroup()
          tasks.push(this.listTierGroups())
        }

        await Promise.all(tasks)
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.isInitialLoading = false
      }
    },

    getTierGroupName(id) {
      const group = this.tierGroups.find(g => g.id === id)
      return group ? group.name : id
    },

    async onClickAddToTierGroup() {
      this.showAddDropdown = true
      try {
        await this.listTierGroups()
      } catch (error) {
        console.error(error)
      }
    },

    cancelAdd() {
      this.showAddDropdown = false
      this.selectedDropdownValue = null
      this.cancelPendingExisting()
    },

    onDropdownChange(value) {
      if (value === CREATE_NEW_OPTION) {
        this.showCreateModal = true
        this.selectedDropdownValue = null
      } else if (value) {
        this.pendingExistingGroupId = value
        this.pendingBounds = { minRpc: null, maxRpc: null }
      }
    },

    cancelPendingExisting() {
      this.pendingExistingGroupId = null
      this.pendingBounds = { minRpc: null, maxRpc: null }
    },

    async confirmAddToExisting() {
      if (!this.canConfirmExisting) {
        return
      }

      this.isLoading = true
      try {
        await this.addCategoryToTierGroup({
          id: this.pendingExistingGroupId,
          categoryId: this.category.id,
          minRpc: this.pendingBounds.minRpc,
          maxRpc: this.pendingBounds.maxRpc
        })
        showSuccessMessage('Category added to tier group.')
        await this.afterMembershipChange(this.pendingExistingGroupId)
        this.showAddDropdown = false
        this.cancelPendingExisting()
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.isLoading = false
      }
    },

    async onCreateNewTierGroup(payload) {
      this.isCreating = true
      try {
        await this.createTierGroup({
          name: payload.name,
          status: payload.status
        })

        // API returns no body on create; refetch and locate the new group by name (server enforces case-insensitive name uniqueness).
        await this.listTierGroups()
        const target = payload.name.toLowerCase()
        const created = this.tierGroups.find(g => g.name && g.name.toLowerCase() === target)
        if (!created?.id) {
          throw new Error(`Tier group "${payload.name}" was created but could not be located.`)
        }

        await this.addCategoryToTierGroup({
          id: created.id,
          categoryId: payload.categoryId,
          minRpc: payload.minRpc,
          maxRpc: payload.maxRpc
        })

        showSuccessMessage(`Tier group "${payload.name}" created.`)
        this.showCreateModal = false
        this.showAddDropdown = false
        await this.afterMembershipChange(created.id)
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.isCreating = false
      }
    },

    async onAddCategoryToGroup({ categoryId, minRpc, maxRpc }) {
      if (!this.currentTierGroup?.id) {
        return
      }

      this.isLoading = true
      try {
        await this.addCategoryToTierGroup({
          id: this.currentTierGroup.id,
          categoryId,
          minRpc,
          maxRpc
        })
        showSuccessMessage('Category added to tier group.')
        await this.findTierGroup(this.currentTierGroup.id)
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.isLoading = false
      }
    },

    async onUpdateTierGroupStatus(status) {
      if (!this.currentTierGroup?.id) {
        return
      }

      this.isLoading = true
      try {
        await this.updateTierGroup({ id: this.currentTierGroup.id, status })
        showSuccessMessage('Tier group status updated.')
        await this.findTierGroup(this.currentTierGroup.id)
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.isLoading = false
      }
    },

    async onRemoveCategory(categoryId) {
      if (!this.currentTierGroup?.id) {
        return
      }

      const groupId = this.currentTierGroup.id
      const isSelf = categoryId === this.category.id
      const wasLast = this.currentTierGroup.categories?.length === 1

      this.isLoading = true
      try {
        await this.removeCategoryFromTierGroup({ id: groupId, categoryId })
        showSuccessMessage('Category removed from tier group.')

        if (isSelf || wasLast) {
          this.$emit('membership-changed')
        } else {
          await this.findTierGroup(groupId)
        }
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.isLoading = false
      }
    },

    async onUpdateRpcBounds({ categoryId, minRpc, maxRpc, done }) {
      if (!this.currentTierGroup?.id) {
        done?.(false)
        return
      }

      this.isLoading = true
      let success = false
      try {
        await this.updateRpcBoundsAction({
          id: this.currentTierGroup.id,
          categoryId,
          minRpc,
          maxRpc
        })
        showSuccessMessage('RPC bounds updated.')
        await this.findTierGroup(this.currentTierGroup.id)
        success = true
      } catch (error) {
        showErrorMessage(error)
      } finally {
        this.isLoading = false
        done?.(success)
      }
    },

    async afterMembershipChange(newGroupId) {
      try {
        await this.findTierGroup(newGroupId)
      } catch (error) {
        console.error(error)
      }

      this.$emit('membership-changed')
    }
  }
}
</script>
